import type { gql } from "@codesandbox/api";
import { useStateTransition } from "@codesandbox/states";
import { useEnvironmentInterface } from "environment-interface";
import { useReducerDevtools } from "features/utils/useReducerDevtools";
import { useSession } from "hooks/api/useSession";
import { hasBranchWriteAccess } from "isomorphic/permission";
import { useEffect, useReducer } from "react";
import type { Dispatch } from "react";

import { createHookContext } from "utils/createHookContext";
import { csbApi } from "utils/csbApi";

import { toSentryContext } from "../utils/sentry";

import type { PublicAction, State } from "./reducer";
import { reducer } from "./reducer";

export type Props = {
  initialState?: State;
  owner: string;
  repo: string;
  workspaceId: string | null;
  defaultBranchName: string;
  onBranchCreateSuccess: (newBranchName: string) => void;
  onBranchCreateError: () => void;
  onFork: (params: {
    owner: string;
    repo: string;
    branch: string;
    workspaceId: string | null;
  }) => void;
  authorization: gql.ProjectAuthorization;
};

export const useBranch = createHookContext(
  ({
    initialState,
    onBranchCreateSuccess,
    onBranchCreateError,
    onFork,
    authorization,
    defaultBranchName,
    owner,
    repo,
    workspaceId,
  }: Props) => {
    const { api, sentry, storage } = useEnvironmentInterface();

    const branchFeature = useReducer(
      reducer,
      initialState || {
        state: "READY",
      },
    );

    const [session] = useSession();

    useReducerDevtools(`branch`, branchFeature);

    const [state, dispatch] = branchFeature;

    useEffect(() => api.events.subscribe(dispatch), []);

    useStateTransition(
      state,
      "CREATING_BRANCH",
      ({ sourceBranchName, newBranchName, workspaceId }) => {
        if (!hasBranchWriteAccess(authorization)) {
          api.forkBranch(owner, repo, workspaceId);
          return;
        }

        api.createBranch({
          owner,
          repo,
          workspaceId: workspaceId ?? null,
          sourceBranch: sourceBranchName ?? defaultBranchName,
          newBranch: newBranchName,
        });
      },
      [authorization],
    );

    useStateTransition(
      state,
      "FORKING_BRANCH",
      ({ workspaceId: forkedWorkspaceId }) => {
        if (session.state !== "AUTHENTICATED") {
          csbApi.session.signIn();

          // If user signs in it will reload the project editor as it needs to fetch the branch again
          // with correct auth
          return;
        }

        api.forkBranch(owner, repo, forkedWorkspaceId);

        // !important: Update the selected workspace
        storage.set("codesandbox-selected-team-id", forkedWorkspaceId);
      },
    );

    useStateTransition(
      state,
      "CREATING_DRAFT_BRANCH",
      ({ sourceBranchName }) => {
        api.createBranch({
          owner,
          repo,
          workspaceId,
          sourceBranch: sourceBranchName ?? defaultBranchName,
        });
      },
    );

    useStateTransition(
      state,
      {
        CREATING_BRANCH: {
          "API:CREATE_BRANCH_SUCCESS": "READY",
        },
        CREATING_DRAFT_BRANCH: {
          "API:CREATE_BRANCH_SUCCESS": "READY",
        },
      },
      (_, { branch: createdBranch }) => {
        const newBranchName = createdBranch.name;
        onBranchCreateSuccess(newBranchName);
      },
    );

    useStateTransition(
      state,
      {
        CREATING_BRANCH: {
          "API:FORK_BRANCH_SUCCESS": "READY",
        },
        FORKING_BRANCH: {
          "API:FORK_BRANCH_SUCCESS": "READY",
        },
      },
      (_, { branch }) => {
        if (workspaceId) {
          // Branches/Forks can only be created on a team, so workspaceId is mandatory
          onFork({
            owner: branch.owner,
            repo: branch.repo,
            branch: branch.name,
            workspaceId,
          });
        }
      },
    );

    useStateTransition(
      state,
      {
        CREATING_BRANCH: {
          "API:CREATE_BRANCH_ERROR": "BRANCH_CREATE_ERROR",
        },
        CREATING_DRAFT_BRANCH: {
          "API:CREATE_BRANCH_ERROR": "BRANCH_CREATE_ERROR",
        },
        FORKING_BRANCH: {
          "API:FORK_BRANCH_ERROR": "BRANCH_CREATE_ERROR",
        },
      },
      ({ message }, action, prevState) => {
        sentry.reportError(
          message,
          toSentryContext("branchManagement", {
            state: prevState.state,
            event: action.type,
          }),
        );

        onBranchCreateError();
      },
    );

    return [state, dispatch] as [State, Dispatch<PublicAction>];
  },
);
